import type {
  BoundingRegion,
  SourceBoundingBox,
  SourceLocation,
} from "@/interface/pdfTypes";

export interface ResolvedHighlightLocations {
  /** Preferred occurrence first, followed by the remaining source occurrences. */
  locationGroups: BoundingRegion[][];
  /** Flattened compatibility view for existing PDF-selection consumers. */
  boundingRegions: BoundingRegion[];
}

/** Convert precise native locations, falling back to one legacy grid-location group. */
export function resolveHighlightLocations(
  sourceLocations: SourceLocation[] | null | undefined,
  preferredSourceLocation: SourceLocation | null | undefined,
  legacyRegions: BoundingRegion[],
): ResolvedHighlightLocations {
  const validLocations = (sourceLocations ?? []).filter(isValidLocation);
  const validPreferred = preferredSourceLocation && isValidLocation(preferredSourceLocation)
    ? preferredSourceLocation
    : undefined;
  if (validLocations.length === 0 && !validPreferred) {
    const fallbackGroups = legacyRegions.length > 0 ? [legacyRegions] : [];
    return {
      locationGroups: fallbackGroups,
      boundingRegions: legacyRegions,
    };
  }

  const orderedLocations = preferredFirst(validLocations, validPreferred);
  const locationGroups = orderedLocations.map(locationToBoundingRegions);
  return {
    locationGroups,
    boundingRegions: locationGroups.flat(),
  };
}

export function locationToBoundingRegions(location: SourceLocation): BoundingRegion[] {
  return location.boxes.filter(isValidBox).map((box) => ({
    page_number: location.page,
    polygon: [
      { x: box.x0, y: box.y0 },
      { x: box.x1, y: box.y0 },
      { x: box.x1, y: box.y1 },
      { x: box.x0, y: box.y1 },
    ],
  }));
}

function preferredFirst(
  locations: SourceLocation[],
  preferred: SourceLocation | null | undefined,
): SourceLocation[] {
  if (!preferred) return locations;
  const preferredIndex = locations.findIndex((location) => sameLocation(location, preferred));
  if (preferredIndex < 0) return [preferred, ...locations];
  if (preferredIndex === 0) return locations;
  return [
    locations[preferredIndex],
    ...locations.slice(0, preferredIndex),
    ...locations.slice(preferredIndex + 1),
  ];
}

function isValidLocation(location: SourceLocation): boolean {
  return Number.isInteger(location?.page)
    && location.page >= 1
    && Array.isArray(location.boxes)
    && location.boxes.some(isValidBox);
}

function isValidBox(box: SourceBoundingBox): boolean {
  return [box?.x0, box?.y0, box?.x1, box?.y1].every(
    (coordinate) => Number.isFinite(coordinate) && coordinate >= 0 && coordinate <= 1,
  ) && box.x1 >= box.x0 && box.y1 >= box.y0;
}

function sameLocation(left: SourceLocation, right: SourceLocation): boolean {
  if (left.page !== right.page || left.boxes.length !== right.boxes.length) return false;
  return left.boxes.every((box, index) => {
    const other = right.boxes[index];
    return other !== undefined
      && box.x0 === other.x0
      && box.y0 === other.y0
      && box.x1 === other.x1
      && box.y1 === other.y1;
  });
}
